import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/router';
import Layout from '@/components/Layout';
import { Incidencia } from '@/types';
import { useAuth } from '@/lib/authContext';
import { supabase } from '@/lib/supabase';
import FilterBar, { Filter } from '@/components/FilterBar';
import IncidentChat from '@/components/IncidentChat';
import DocumentViewer from '@/components/DocumentViewer';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

export default function AsesorIncidencias() {
  const { user, loading } = useAuth();
  const router = useRouter();
  const [incidencias, setIncidencias] = useState<any[]>([]);
  const [clientesAsignados, setClientesAsignados] = useState<any[]>([]);
  const [filtros, setFiltros] = useState<Record<string, any>>({});
  const [incidenciaSeleccionada, setIncidenciaSeleccionada] = useState<any | null>(null);
  const [mostrarChat, setMostrarChat] = useState(false);
  const [mostrarNuevaIncidencia, setMostrarNuevaIncidencia] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [clienteSeleccionado, setClienteSeleccionado] = useState<string | null>(null);
  const [vistaActual, setVistaActual] = useState<'clientes' | 'incidencias'>('clientes');
  
  // Form state for new incident
  const [selectedClienteId, setSelectedClienteId] = useState<string>('');
  const [selectedDocumentoId, setSelectedDocumentoId] = useState<string>('');
  const [descripcion, setDescripcion] = useState<string>('');
  const [documentosDisponibles, setDocumentosDisponibles] = useState<any[]>([]);

  // Define handleMessageAdded before it's used in useEffect
  const handleMessageAdded = useCallback((nuevoMensaje: any) => {
    // Update the selected incident with the new message
    if (incidenciaSeleccionada) {
      // Check if message already exists to prevent duplicates
      const messageExists = incidenciaSeleccionada.mensajes?.some(
        (msg: any) => msg.id === nuevoMensaje.id
      );

      if (messageExists) {
        console.log('Message already exists, skipping:', nuevoMensaje.id);
        return;
      }

      const updatedIncidencia = {
        ...incidenciaSeleccionada,
        mensajes: [...(incidenciaSeleccionada.mensajes || []), nuevoMensaje],
        fechaUltimaRespuesta: nuevoMensaje.fecha,
      };
      setIncidenciaSeleccionada(updatedIncidencia);

      // Also update the incident in the main list
      setIncidencias(prevIncidencias =>
        prevIncidencias.map(inc =>
          inc.id === incidenciaSeleccionada.id
            ? updatedIncidencia
            : inc
        )
      );
    }
  }, [incidenciaSeleccionada]);

  // Load documents when client is selected
  useEffect(() => {
    if (selectedClienteId) {
      loadDocumentosByCliente(selectedClienteId);
    } else {
      setDocumentosDisponibles([]);
    }
  }, [selectedClienteId]);

  // Reset form when modal is closed (but keep selected client if viewing client-specific view)
  useEffect(() => {
    if (!mostrarNuevaIncidencia) {
      // Only reset client selection if not in client-specific view
      if (vistaActual !== 'incidencias') {
        setSelectedClienteId('');
      }
      setSelectedDocumentoId('');
      setDescripcion('');
      setDocumentosDisponibles([]);
    }
  }, [mostrarNuevaIncidencia, vistaActual]);

  // Pre-select client when opening new incident form from client-specific view
  useEffect(() => {
    if (mostrarNuevaIncidencia && vistaActual === 'incidencias' && clienteSeleccionado) {
      setSelectedClienteId(clienteSeleccionado);
    }
  }, [mostrarNuevaIncidencia, vistaActual, clienteSeleccionado]);

  // Real-time subscription for messages when viewing an incident
  useEffect(() => {
    if (!incidenciaSeleccionada?.id) return;

    console.log('[RT] Setting up real-time subscription for incident:', incidenciaSeleccionada.id);

    // Subscribe to new messages for this incident
    const channel = supabase
      .channel(`incidencia-asesor-${incidenciaSeleccionada.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'mensajes_incidencia',
          filter: `incidencia_id=eq.${incidenciaSeleccionada.id}`,
        },
        async (payload) => {
          console.log('[RT] New message received via real-time:', payload);
          
          // Transform the new message to match the expected format
          const newMessage = {
            id: payload.new.id,
            usuarioId: payload.new.usuario_id,
            mensaje: payload.new.mensaje,
            rol: payload.new.rol,
            fecha: new Date(payload.new.fecha),
            archivosAdjuntos: payload.new.archivos_adjuntos || [],
            esIA: payload.new.es_ia || false,
          };

          // Only add message from other users (current user's messages are added via onMessageAdded callback)
          if (newMessage.usuarioId !== user?.id) {
            console.log('[RT] Adding message from other user');
            handleMessageAdded(newMessage);
          } else {
            console.log('[RT] Skipping own message (already added via callback)');
          }
        }
      )
      .subscribe((status, err) => {
        if (status === 'SUBSCRIBED') {
          console.log('[RT] Real-time subscription active for incident');
        }
        if (err) {
          console.error('[RT] Real-time subscription error:', err);
          toast.error('Error al conectar chat en tiempo real');
        }
      });

    // Cleanup subscription on unmount or when incident changes
    return () => {
      console.log('[RT] Cleaning up real-time subscription for incident');
      supabase.removeChannel(channel);
    };
  }, [incidenciaSeleccionada?.id, user?.id, handleMessageAdded]);

  const loadIncidencias = useCallback(async () => {
    try {
      setLoadingData(true);

      // Get advisor's assigned clients
      const { data: assignedClients, error: clientError } = await supabase
        .from('asesor_cliente')
        .select('cliente_id, clientes(id, razon_social, nombre_comercial)')
        .eq('asesor_id', user?.id);

      if (clientError) throw clientError;

      const clientes = assignedClients?.map(ac => ac.clientes).filter(Boolean) || [];
      const clientIds = assignedClients?.map(ac => ac.cliente_id) || [];
      setClientesAsignados(clientes);

      // Get incidents for these clients
      const { data: incidents, error: incidentsError } = await supabase
        .from('incidencias')
        .select(`
          *,
          clientes(id, razon_social, nombre_comercial),
          documentos(id, nombre, tipo, estado, fecha_subida, procesado_ia, tamanio, url_archivo, nro_asiento_erp, datos_ia, cliente_id, asesor_id),
          mensajes_incidencia(
            id,
            usuario_id,
            mensaje,
            rol,
            fecha
          )
        `)
        .in('cliente_id', clientIds)
        .order('fecha_creacion', { ascending: false });

      if (incidentsError) throw incidentsError;

      // Transform data to match expected format
      const transformedIncidents = incidents?.map(inc => ({
        id: inc.id,
        documentoId: inc.documento_id,
        clienteId: inc.cliente_id,
        asesorId: inc.asesor_id,
        estado: inc.estado,
        origen: inc.origen,
        fechaCreacion: inc.fecha_creacion,
        fechaUltimaRespuesta: inc.fecha_ultima_respuesta,
        cliente: inc.clientes,
        documento: inc.documentos ? {
          id: inc.documentos.id,
          nombre: inc.documentos.nombre,
          clienteId: inc.documentos.cliente_id,
          asesorId: inc.documentos.asesor_id,
          tipo: inc.documentos.tipo,
          estado: inc.documentos.estado,
          fechaSubida: new Date(inc.documentos.fecha_subida),
          procesadoIA: inc.documentos.procesado_ia,
          tamaño: inc.documentos.tamanio,
          urlArchivo: inc.documentos.url_archivo,
          nroAsientoERP: inc.documentos.nro_asiento_erp,
          datosIA: inc.documentos.datos_ia,
        } : null,
        mensajes: inc.mensajes_incidencia?.map((msg: any) => ({
          id: msg.id,
          usuarioId: msg.usuario_id,
          mensaje: msg.mensaje,
          rol: msg.rol,
          fecha: msg.fecha,
          archivosAdjuntos: msg.archivos_adjuntos || [],
          esIA: msg.es_ia || false,
        })) || [],
      })) || [];

      setIncidencias(transformedIncidents);
    } catch (error) {
      console.error('Error loading incidents:', error);
      toast.error('Error al cargar incidencias');
    } finally {
      setLoadingData(false);
    }
  }, [user]);

  // Real-time subscription for incident updates in the list
  useEffect(() => {
    if (!user?.id) return;

    console.log('[RT] Setting up real-time subscription for incidents list');

    // Subscribe to incidencias updates (status changes, new incidents)
    const incidenciasChannel = supabase
      .channel(`asesor-incidencias-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'incidencias',
        },
        async () => {
          console.log('[RT] Incidencias list updated, refreshing...');
          await loadIncidencias();
        }
      )
      .subscribe((status, err) => {
        if (status === 'SUBSCRIBED') {
          console.log('[RT] Incidencias list subscription active');
        }
        if (err) {
          console.error('[RT] Incidencias list subscription error:', err);
        }
      });

    // Cleanup subscription
    return () => {
      console.log('[RT] Cleaning up incidencias list subscription');
      supabase.removeChannel(incidenciasChannel);
    };
  }, [user?.id, loadIncidencias]);

  // Load data on mount
  useEffect(() => {
    if (!loading && (!user || user.rol !== 'asesor')) {
      router.push('/');
    }
    if (user && user.rol === 'asesor') {
      loadIncidencias();
    }
  }, [user, loading, router, loadIncidencias]);

  const loadDocumentosByCliente = async (clienteId: string) => {
    try {
      const { data: docs, error } = await supabase
        .from('documentos')
        .select('id, nombre')
        .eq('cliente_id', clienteId)
        .order('fecha_subida', { ascending: false });

      if (error) throw error;
      setDocumentosDisponibles(docs || []);
    } catch (error) {
      console.error('Error loading documents:', error);
      toast.error('Error al cargar documentos');
    }
  };

  const filterConfig: Filter[] = [
    {
      key: 'buscar',
      label: 'Buscar',
      type: 'text',
      placeholder: 'ID incidencia, cliente...',
    },
    {
      key: 'cliente',
      label: 'Cliente',
      type: 'select',
      options: clientesAsignados.map((c: any) => ({ value: c.id, label: c.razon_social })),
    },
    {
      key: 'estado',
      label: 'Estado',
      type: 'select',
      options: [
        { value: 'nueva', label: 'Nueva' },
        { value: 'en_revision', label: 'En Revisión' },
        { value: 'resuelta', label: 'Resuelta' },
        { value: 'cerrada', label: 'Cerrada' },
      ],
    },
  ];

  const incidenciasFiltradas = incidencias.filter(inc => {
    if (filtros.buscar) {
      const busqueda = filtros.buscar.toLowerCase();
      const idStr = inc.id?.toString().toLowerCase() || '';
      const razonSocial = inc.cliente?.razon_social?.toLowerCase() || '';
      if (!idStr.includes(busqueda) && !razonSocial.includes(busqueda)) {
        return false;
      }
    }
    if (filtros.cliente && inc.clienteId !== filtros.cliente) return false;
    if (filtros.estado && inc.estado !== filtros.estado) return false;
    return true;
  });

  const handleFiltroChange = (key: string, value: any) => {
    setFiltros({ ...filtros, [key]: value });
  };

  const handleLimpiarFiltros = () => {
    setFiltros({});
  };

  const handleVerIncidencia = (inc: any) => {
    setIncidenciaSeleccionada(inc);
    setMostrarChat(true);
  };

  const estadisticas = {
    total: incidencias.length,
    nuevas: incidencias.filter(i => i.estado === 'nueva').length,
    enRevision: incidencias.filter(i => i.estado === 'en_revision').length,
    resueltas: incidencias.filter(i => i.estado === 'resuelta').length,
  };

  // Calculate incident stats per client
  interface ClienteStats {
    cliente: any;
    totalIncidencias: number;
    nuevas: number;
    enRevision: number;
    resueltas: number;
    ultimaIncidencia?: Date;
  }

  const getClienteStats = (): ClienteStats[] => {
    const statsMap = new Map<string, ClienteStats>();

    // Initialize stats for all clients
    clientesAsignados.forEach((cliente) => {
      statsMap.set(cliente.id, {
        cliente,
        totalIncidencias: 0,
        nuevas: 0,
        enRevision: 0,
        resueltas: 0,
      });
    });

    // Count incidents by client
    incidencias.forEach((inc) => {
      const stats = statsMap.get(inc.clienteId);
      if (stats) {
        stats.totalIncidencias++;
        if (inc.estado === 'nueva') {
          stats.nuevas++;
        } else if (inc.estado === 'en_revision') {
          stats.enRevision++;
        } else if (inc.estado === 'resuelta') {
          stats.resueltas++;
        }
        const incDate = new Date(inc.fechaCreacion);
        if (!stats.ultimaIncidencia || incDate > stats.ultimaIncidencia) {
          stats.ultimaIncidencia = incDate;
        }
      }
    });

    // Convert to array and sort by pending incidents first
    return Array.from(statsMap.values())
      .sort((a, b) => {
        // Sort by new + in_revision incidents first (descending), then by total
        const aPendientes = a.nuevas + a.enRevision;
        const bPendientes = b.nuevas + b.enRevision;
        if (bPendientes !== aPendientes) {
          return bPendientes - aPendientes;
        }
        return b.totalIncidencias - a.totalIncidencias;
      });
  };

  const handleClienteClick = (clienteId: string) => {
    setClienteSeleccionado(clienteId);
    setVistaActual('incidencias');
  };

  const handleVolverAClientes = () => {
    setClienteSeleccionado(null);
    setVistaActual('clientes');
  };

  const getIncidenciasDelCliente = (clienteId: string): any[] => {
    return incidencias
      .filter((inc) => inc.clienteId === clienteId)
      .sort((a, b) => {
        // Sort by status priority (nueva > en_revision > resuelta)
        const statusPriority: any = { nueva: 0, en_revision: 1, resuelta: 2, cerrada: 3 };
        const aPriority = statusPriority[a.estado] ?? 999;
        const bPriority = statusPriority[b.estado] ?? 999;
        if (aPriority !== bPriority) return aPriority - bPriority;
        // Then by date (most recent first)
        return new Date(b.fechaCreacion).getTime() - new Date(a.fechaCreacion).getTime();
      });
  };

  const getClienteById = (id: string): any | undefined => {
    return clientesAsignados.find(c => c.id === id);
  };

  const getEstadoBadge = (estado: string) => {
    switch (estado) {
      case 'nueva': return { label: 'Nueva', icon: 'schedule', className: 'bg-amber-50 text-amber-700 border-amber-200' };
      case 'en_revision': return { label: 'En Revisión', icon: 'pending', className: 'bg-blue-50 text-blue-700 border-blue-200' };
      case 'resuelta': return { label: 'Resuelta', icon: 'check_circle', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
      default: return { label: 'Cerrada', icon: 'cancel', className: 'bg-calm-bg text-calm-muted border-calm-border' };
    }
  };

  const getOrigenBadge = (origen: string) => {
    switch (origen) {
      case 'ia': return { label: 'IA', icon: 'smart_toy', className: 'bg-blue-50 text-blue-700 border-blue-200' };
      case 'asesor': return { label: 'Asesor', icon: 'person', className: 'bg-indigo-50 text-indigo-700 border-indigo-200' };
      default: return { label: 'Manual', icon: 'edit_note', className: 'bg-amber-50 text-amber-700 border-amber-200' };
    }
  };

  const clienteStats = getClienteStats();

  return (
    <Layout rol="asesor">
      <div className="h-screen overflow-y-auto scrollbar-thin">
        <div className="px-12 pb-12 space-y-12">

          {/* ──────── Header ──────── */}
          <div className="flex justify-between items-end">
            <div>
              <h1 className="text-5xl font-display font-light text-calm-text">Incidencias</h1>
              <p className="text-calm-muted font-light mt-2">Gestión de incidencias por cliente</p>
            </div>
            <Button
              onClick={() => setMostrarNuevaIncidencia(true)}
              className="bg-calm-text text-white rounded-huge px-5 py-2.5 text-sm font-light hover:bg-calm-text/90 transition-colors flex items-center gap-2"
            >
              <span className="material-symbols-outlined text-[16px]">add</span>
              Nueva Incidencia
            </Button>
          </div>

          {/* ──────── Metric Cards ──────── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-white to-amber-50/20 border border-amber-200/40 rounded-huge p-8">
              <div className="flex items-start justify-between">
                <div className="space-y-3">
                  <p className="text-[10px] uppercase tracking-widest text-calm-muted">Total</p>
                  <p className="text-2xl font-light text-calm-text">{estadisticas.total}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-calm-accent">
                  <span className="material-symbols-outlined text-xl">folder_open</span>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge p-8">
              <div className="flex items-start justify-between">
                <div className="space-y-3">
                  <p className="text-[10px] uppercase tracking-widest text-calm-muted">Nuevas</p>
                  <p className="text-2xl font-light text-amber-600">{estadisticas.nuevas}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-fresh-sky-50 flex items-center justify-center text-fresh-sky-500">
                  <span className="material-symbols-outlined text-xl">schedule</span>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-white to-emerald-50/20 border border-emerald-200/40 rounded-huge p-8">
              <div className="flex items-start justify-between">
                <div className="space-y-3">
                  <p className="text-[10px] uppercase tracking-widest text-calm-muted">En Revisión</p>
                  <p className="text-2xl font-light text-blue-600">{estadisticas.enRevision}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-500">
                  <span className="material-symbols-outlined text-xl">pending</span>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8">
              <div className="flex items-start justify-between">
                <div className="space-y-3">
                  <p className="text-[10px] uppercase tracking-widest text-calm-muted">Resueltas</p>
                  <p className="text-2xl font-light text-emerald-600">{estadisticas.resueltas}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-500">
                  <span className="material-symbols-outlined text-xl">check_circle</span>
                </div>
              </div>
            </div>
          </div>

          {/* ──────── Content ──────── */}
          {vistaActual === 'clientes' ? (
            /* ── Client cards view ── */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {loadingData ? (
                <div className="col-span-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <Skeleton className="h-48 rounded-huge" />
                  <Skeleton className="h-48 rounded-huge" />
                  <Skeleton className="h-48 rounded-huge" />
                </div>
              ) : clienteStats.length === 0 ? (
                <div className="col-span-full text-center py-16">
                  <span className="material-symbols-outlined text-6xl text-calm-accent block mb-4">group</span>
                  <h3 className="text-xl font-display font-light text-calm-text mb-2">
                    No tienes clientes asignados
                  </h3>
                  <p className="text-calm-muted font-light">
                    No hay clientes asignados a tu cuenta.
                  </p>
                </div>
              ) : (
                clienteStats.map((stats) => {
                  const iniciales = stats.cliente.razon_social
                    .split(' ')
                    .map((palabra: string) => palabra[0])
                    .join('')
                    .substring(0, 2)
                    .toUpperCase();

                  return (
                    <div
                      key={stats.cliente.id}
                      className="bg-gradient-to-br from-white to-amber-50/20 border border-amber-200/40 rounded-huge p-8 hover:shadow-sm transition-all cursor-pointer"
                      onClick={() => handleClienteClick(stats.cliente.id)}
                    >
                      <div className="flex items-start space-x-4 mb-6">
                        <Avatar className="h-11 w-11">
                          <AvatarFallback className="bg-fresh-sky-50 text-calm-text font-light text-sm">
                            {iniciales}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <h3 className="font-light text-calm-text">
                            {stats.cliente.razon_social}
                          </h3>
                          <p className="text-sm text-calm-muted font-light">
                            {stats.cliente.nombre_comercial || 'Sin nombre comercial'}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-[10px] uppercase tracking-widest text-calm-muted">Última incidencia</span>
                          <span className="text-sm font-light text-calm-text">
                            {stats.ultimaIncidencia
                              ? `hace ${Math.floor(
                                  (Date.now() - stats.ultimaIncidencia.getTime()) /
                                    (1000 * 60 * 60 * 24)
                                )} días`
                              : '—'}
                          </span>
                        </div>
                      </div>

                      <Separator className="bg-calm-border my-5" />

                      <div className="grid grid-cols-3 gap-2">
                        <div className="text-center">
                          <div className="text-2xl font-light text-amber-600">
                            {stats.nuevas}
                          </div>
                          <div className="text-[10px] uppercase tracking-widest text-calm-muted mt-1">nuevas</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-light text-blue-600">
                            {stats.enRevision}
                          </div>
                          <div className="text-[10px] uppercase tracking-widest text-calm-muted mt-1">en revisión</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-light text-emerald-600">
                            {stats.resueltas}
                          </div>
                          <div className="text-[10px] uppercase tracking-widest text-calm-muted mt-1">resueltas</div>
                        </div>
                      </div>

                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClienteClick(stats.cliente.id);
                        }}
                        variant="ghost"
                        className="w-full mt-6 border border-calm-border rounded-huge px-5 py-2.5 text-sm font-light text-calm-muted hover:bg-calm-bg hover:text-calm-text transition-all"
                      >
                        Ver Incidencias
                      </Button>
                    </div>
                  );
                })
              )}
            </div>
          ) : (
            /* ── Incidents view for selected client ── */
            <>
              {clienteSeleccionado && (
                <>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={handleVolverAClientes}
                        className="border border-calm-border rounded-huge px-5 py-2.5 text-sm font-light text-calm-muted hover:bg-white hover:text-calm-text transition-all flex items-center gap-2"
                      >
                        <span className="material-symbols-outlined text-[16px]">arrow_back</span>
                        Volver a Clientes
                      </button>
                      <div>
                        <h2 className="text-xl font-display font-light text-calm-text">
                          {getClienteById(clienteSeleccionado)?.razon_social}
                        </h2>
                        <p className="text-sm text-calm-muted font-light">
                          {getIncidenciasDelCliente(clienteSeleccionado).length} incidencias
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={() => setMostrarNuevaIncidencia(true)}
                      className="bg-calm-text text-white rounded-huge px-5 py-2.5 text-sm font-light hover:bg-calm-text/90 transition-colors flex items-center gap-2"
                    >
                      <span className="material-symbols-outlined text-[16px]">add</span>
                      Nueva Incidencia
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {getIncidenciasDelCliente(clienteSeleccionado).map((inc) => {
                      const cliente = inc.cliente;
                      const estadoBadge = getEstadoBadge(inc.estado);
                      const origenBadge = getOrigenBadge(inc.origen);
                      return (
                        <div
                          key={inc.id}
                          className="bg-gradient-to-br from-white to-amber-50/20 border border-amber-200/40 rounded-huge p-8 hover:shadow-sm transition-all cursor-pointer"
                          onClick={() => handleVerIncidencia(inc)}
                        >
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h3 className="font-light text-calm-text">#{inc.id.substring(0, 8)}</h3>
                              <p className="text-sm text-calm-muted font-light">{cliente?.razon_social}</p>
                            </div>
                            <Badge variant="outline" className={cn('rounded-full gap-1.5 font-light', estadoBadge.className)}>
                              <span className="material-symbols-outlined text-[12px]">{estadoBadge.icon}</span>
                              {estadoBadge.label}
                            </Badge>
                          </div>

                          <div className="space-y-3 text-sm">
                            <div className="flex justify-between items-center">
                              <span className="text-calm-muted font-light">Origen</span>
                              <Badge variant="outline" className={cn('rounded-full gap-1 text-xs font-light', origenBadge.className)}>
                                <span className="material-symbols-outlined text-[11px]">{origenBadge.icon}</span>
                                {origenBadge.label}
                              </Badge>
                            </div>

                            <div className="flex justify-between">
                              <span className="text-calm-muted font-light">Mensajes</span>
                              <span className="font-light text-calm-text">{inc.mensajes.length}</span>
                            </div>

                            <div className="flex justify-between">
                              <span className="text-calm-muted font-light">Creada</span>
                              <span className="font-light text-calm-text" suppressHydrationWarning>
                                {new Date(inc.fechaCreacion).toLocaleDateString('es-ES')}
                              </span>
                            </div>

                            {inc.fechaUltimaRespuesta && (
                              <div className="flex justify-between">
                                <span className="text-calm-muted font-light">Última actualización</span>
                                <span className="font-light text-calm-text" suppressHydrationWarning>
                                  {new Date(inc.fechaUltimaRespuesta).toLocaleDateString('es-ES')}
                                </span>
                              </div>
                            )}
                          </div>

                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleVerIncidencia(inc);
                            }}
                            variant="ghost"
                            className="w-full mt-6 border border-calm-border rounded-huge px-5 py-2.5 text-sm font-light text-calm-muted hover:bg-calm-bg hover:text-calm-text transition-all flex items-center justify-center gap-2"
                          >
                            <span className="material-symbols-outlined text-[16px]">chat</span>
                            Ver Conversación
                          </Button>
                        </div>
                      );
                    })}

                    {getIncidenciasDelCliente(clienteSeleccionado).length === 0 && (
                      <div className="col-span-full text-center py-16">
                        <span className="material-symbols-outlined text-6xl text-calm-accent block mb-4">warning</span>
                        <h3 className="text-xl font-display font-light text-calm-text mb-2">
                          No hay incidencias
                        </h3>
                        <p className="text-calm-muted font-light">
                          Este cliente no tiene incidencias registradas.
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}

        </div>
      </div>

      {/* ──────── Chat Dialog ──────── */}
      {incidenciaSeleccionada && (
        <Dialog open={mostrarChat} onOpenChange={(open) => {
          if (!open) {
            setMostrarChat(false);
            setIncidenciaSeleccionada(null);
          }
        }}>
          <DialogContent className="max-w-5xl h-[80vh] flex flex-col p-0 rounded-huge border-calm-border">
            <DialogHeader className="p-6 pb-4">
              <div className="flex items-center space-x-3">
                <DialogTitle className="text-2xl font-display font-light text-calm-text">
                  Incidencia #{incidenciaSeleccionada.id?.substring(0, 8)}
                </DialogTitle>
                <Badge variant="outline" className={cn('rounded-full gap-1.5 font-light', getEstadoBadge(incidenciaSeleccionada.estado).className)}>
                  <span className="material-symbols-outlined text-[12px]">{getEstadoBadge(incidenciaSeleccionada.estado).icon}</span>
                  {getEstadoBadge(incidenciaSeleccionada.estado).label}
                </Badge>
              </div>
              <DialogDescription className="flex items-center space-x-4 text-sm font-light text-calm-muted">
                <span className="flex items-center gap-1">
                  <span className="material-symbols-outlined text-[14px]">description</span>
                  {incidenciaSeleccionada.documento?.nombre || 'Sin documento'}
                </span>
                <span className="text-calm-accent">·</span>
                <span suppressHydrationWarning>Creada el {new Date(incidenciaSeleccionada.fechaCreacion).toLocaleDateString('es-ES')}</span>
                <span className="text-calm-accent">·</span>
                <span className="flex items-center gap-1">
                  Origen:
                  <span className="material-symbols-outlined text-[14px]">
                    {incidenciaSeleccionada.origen === 'ia' ? 'smart_toy' :
                     incidenciaSeleccionada.origen === 'asesor' ? 'person' : 'edit_note'}
                  </span>
                  {incidenciaSeleccionada.origen === 'ia' ? 'IA' :
                   incidenciaSeleccionada.origen === 'asesor' ? 'Asesor' : 'Manual'}
                </span>
              </DialogDescription>
            </DialogHeader>

            {/* Split view: Chat + Document */}
            <div className="flex-1 flex overflow-hidden border-t border-calm-border">
              {/* Chat */}
              <div className="flex-1 flex flex-col">
                <div className="flex-1 overflow-y-auto">
                  <IncidentChat 
                    incidencia={incidenciaSeleccionada} 
                    currentUserRole="asesor"
                    onMessageAdded={handleMessageAdded}
                  />
                </div>
              </div>

              {/* Document preview */}
              <div className="w-96 border-l border-calm-border">
                {incidenciaSeleccionada.documento ? (
                  <DocumentViewer 
                    documento={incidenciaSeleccionada.documento}
                  />
                ) : (
                  <div className="h-full flex items-center justify-center text-calm-muted">
                    <div className="text-center space-y-2">
                      <span className="material-symbols-outlined text-4xl text-calm-accent block">description</span>
                      <p className="text-sm font-light">Sin documento asociado</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <DialogFooter className="p-4 border-t border-calm-border">
              <div className="flex justify-between items-center w-full">
                <div className="text-sm font-light text-calm-muted">
                  {incidenciaSeleccionada.mensajes?.length || 0} mensaje{(incidenciaSeleccionada.mensajes?.length || 0) !== 1 ? 's' : ''}
                </div>
                <div className="flex space-x-2">
                  {incidenciaSeleccionada.estado !== 'resuelta' && incidenciaSeleccionada.estado !== 'cerrada' && (
                    <Button
                      onClick={async () => {
                        try {
                          await supabase
                            .from('incidencias')
                            .update({ estado: 'resuelta' })
                            .eq('id', incidenciaSeleccionada.id);
                          toast.success('Incidencia marcada como resuelta');
                          await loadIncidencias();
                          setMostrarChat(false);
                        } catch (error) {
                          console.error('Error updating incident:', error);
                          toast.error('Error al actualizar la incidencia');
                        }
                      }}
                      className="bg-emerald-600 text-white rounded-huge px-5 py-2.5 text-sm font-light hover:bg-emerald-700 transition-colors flex items-center gap-2"
                    >
                      <span className="material-symbols-outlined text-[16px]">check_circle</span>
                      Marcar como Resuelta
                    </Button>
                  )}
                  <Button
                    onClick={() => setMostrarChat(false)}
                    variant="ghost"
                    className="border border-calm-border rounded-huge px-5 py-2.5 text-sm font-light text-calm-muted hover:bg-calm-bg hover:text-calm-text transition-all"
                  >
                    Cerrar Vista
                  </Button>
                </div>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ──────── New Incident Dialog ──────── */}
      <Dialog open={mostrarNuevaIncidencia} onOpenChange={setMostrarNuevaIncidencia}>
        <DialogContent className="max-w-2xl rounded-huge border-calm-border">
          <DialogHeader>
            <DialogTitle className="text-2xl font-display font-light text-calm-text">Nueva Incidencia</DialogTitle>
            <DialogDescription className="text-sm font-light text-calm-muted">
              Crea una nueva incidencia seleccionando el cliente y documento afectado.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="space-y-2">
              <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">
                Cliente <span className="text-red-400">*</span>
              </Label>
              <Select
                value={selectedClienteId || undefined}
                onValueChange={setSelectedClienteId}
              >
                <SelectTrigger className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light">
                  <SelectValue placeholder="Seleccione un cliente..." />
                </SelectTrigger>
                <SelectContent>
                  {clientesAsignados.map((c: any) => (
                    <SelectItem key={c.id} value={c.id}>{c.razon_social}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {clientesAsignados.length === 0 && (
                <p className="text-sm font-light text-calm-muted flex items-center gap-1">
                  <span className="material-symbols-outlined text-[14px]">warning</span>
                  No tienes clientes asignados
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Documento (opcional)</Label>
              <Select
                value={selectedDocumentoId || undefined}
                onValueChange={setSelectedDocumentoId}
                disabled={!selectedClienteId}
              >
                <SelectTrigger className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light">
                  <SelectValue placeholder="Sin documento relacionado" />
                </SelectTrigger>
                <SelectContent>
                  {documentosDisponibles.map((d: any) => (
                    <SelectItem key={d.id} value={d.id}>{d.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedClienteId && documentosDisponibles.length === 0 && (
                <p className="text-sm font-light text-calm-muted">
                  Este cliente no tiene documentos cargados
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">
                Descripción <span className="text-red-400">*</span>
              </Label>
              <Textarea
                rows={5}
                placeholder="Describe la incidencia..."
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light resize-none"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              onClick={async () => {
                // Validation
                if (!selectedClienteId) {
                  toast.error('Por favor selecciona un cliente');
                  return;
                }
                if (!descripcion.trim()) {
                  toast.error('Por favor escribe una descripción');
                  return;
                }

                try {
                  // Create incident in database
                  const { data: incidencia, error: incError } = await supabase
                    .from('incidencias')
                    .insert({
                      documento_id: selectedDocumentoId || null,
                      cliente_id: selectedClienteId,
                      asesor_id: user?.id,
                      estado: 'nueva',
                      origen: 'asesor',
                      fecha_creacion: new Date().toISOString(),
                    })
                    .select()
                    .single();

                  if (incError) throw incError;

                  // Create first message
                  const { error: msgError } = await supabase
                    .from('mensajes_incidencia')
                    .insert({
                      incidencia_id: incidencia.id,
                      usuario_id: user?.id,
                      mensaje: descripcion,
                      fecha: new Date().toISOString(),
                    });

                  if (msgError) throw msgError;

                  toast.success('Incidencia creada correctamente');
                  setMostrarNuevaIncidencia(false);
                  loadIncidencias(); // Reload incidents
                } catch (error) {
                  console.error('Error creating incident:', error);
                  toast.error('Error al crear la incidencia');
                }
              }}
              disabled={!selectedClienteId || !descripcion.trim()}
              className="flex-1 bg-calm-text text-white rounded-huge px-5 py-2.5 text-sm font-light hover:bg-calm-text/90 transition-colors"
            >
              Crear Incidencia
            </Button>
            <Button
              onClick={() => setMostrarNuevaIncidencia(false)}
              variant="ghost"
              className="flex-1 border border-calm-border rounded-huge px-5 py-2.5 text-sm font-light text-calm-muted hover:bg-calm-bg hover:text-calm-text transition-all"
            >
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
